import { IPublicClientApplication } from '@azure/msal-browser';
import { acquireBrowserTokenAsync, acquireBrowserTokenSilentOrPopupAsync } from './TokenAcquisitionService';
import { logoutRequest, azureAdScopes, nexusAzureAdScopes } from './msalConfig';

export interface EnsureBrowserAuthenticatedSessionArgs {
    msalInstance: IPublicClientApplication;
}

export const ensureBrowserAuthenticatedSessionAsync = async ({
    msalInstance,
}: EnsureBrowserAuthenticatedSessionArgs): Promise<boolean> => {
    // navigateToLoginRequestUrl: false prevents MSAL from restoring the pre-redirect URL.
    // Without it, the ?state= echoed back by AAD after logoutRedirect gets saved as the
    // "loginRequestUrl" and re-applied to the window URL once login completes.
    const redirectResponse = await msalInstance
        .handleRedirectPromise({ navigateToLoginRequestUrl: false })
        .catch(() => null);

    if (redirectResponse?.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
        return true;
    }

    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) return true;

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        return true;
    }

    // Try AAD session-based silent SSO (no popup).
    try {
        const silent = await msalInstance.ssoSilent({
            scopes: azureAdScopes,
        });

        if (silent.account) {
            msalInstance.setActiveAccount(silent.account);
            return true;
        }
    } catch {

        return false;
    }

    return false;
};

export interface LoginBrowserAsyncArgs {
    msalInstance: IPublicClientApplication;
}

export const loginBrowserAsync = async ({ msalInstance }: LoginBrowserAsyncArgs): Promise<void> => {
    // redirectStartPage is pinned to the origin so that MSAL never captures a dirty URL
    // (e.g. the ?state= AAD echoes back after logoutRedirect) as the post-login return URL.
    await msalInstance.loginRedirect({
        scopes: azureAdScopes,
        redirectStartPage: window.location.origin,
    });
};

export interface AcquireBrowserAccessTokenArgs {
    msalInstance: IPublicClientApplication;
}

export const acquireBrowserAccessTokenAsync = async ({
    msalInstance,
}: AcquireBrowserAccessTokenArgs): Promise<string> => {
    return await acquireBrowserTokenAsync(msalInstance, azureAdScopes);
};

export const acquireNexusBrowserAccessTokenAsync = async ({
    msalInstance,
}: AcquireBrowserAccessTokenArgs): Promise<string> => {
    return await acquireBrowserTokenSilentOrPopupAsync(msalInstance, nexusAzureAdScopes);
};

export interface LogoutBrowserAsyncArgs {
    msalInstance: IPublicClientApplication;
}

export const logoutBrowserAsync = async ({ msalInstance }: LogoutBrowserAsyncArgs): Promise<void> => {
    await msalInstance.logoutRedirect(logoutRequest);
};
